import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button, Badge, ProgressBar} from 'react-bootstrap'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useSocket} from '../../../contexts/SocketContext'
import {getImportJobs, retryImportJob, cancelImportJob, revertImportJob, revertComtradeJob} from './core/_requests'
import {Toast} from '../../../../ui-kit'
import moment from 'moment'
import {showSwal} from '../../../../ui-kit'



const FileUploadQueuePage: FC = () => {
  const axiosPrivate = useAxiosPrivate()
  const {socket} = useSocket()
  const [jobs, setJobs] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const fetchJobs = async () => {
    setIsLoading(true)
    try {
      const res = await getImportJobs(axiosPrivate)
      setJobs(res.data.data || [])
    } catch (err) {
      console.error(err)
      Toast('Error when fetching jobs', 'danger', '')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()

    if (socket) {
      socket.emit('join-room', 'jobs')

      const handleJobUpdated = (data: any) => {
        setJobs((prevJobs) => {
          const jobExists = prevJobs.some((j) => j.id === data.id)
          if (!jobExists) {
            fetchJobs()
            return prevJobs
          }

          if (data.status === 'completed' || data.status === 'failed') {
            fetchJobs()
            return prevJobs
          }

          return prevJobs.map((job) => {
            if (job.id === data.id) {
              return {
                ...job,
                progress: data.progress,
                status: data.status || job.status,
                failedReason: data.failedReason || job.failedReason,
                finishedOn: data.finishedOn || job.finishedOn,
                processedOn: data.processedOn || job.processedOn,
              }
            }
            return job
          })
        })
      }

      socket.on('job-updated', handleJobUpdated)

      return () => {
        socket.emit('leave-room', 'jobs')
        socket.off('job-updated', handleJobUpdated)
      }
    }
  }, [socket])

  const handleRetry = async (id: string | number) => {
    try {
      await retryImportJob(axiosPrivate, id)
      Toast('Job retry initiated successfully', 'success', '')
      fetchJobs()
    } catch (err: any) {
      console.error(err)
      const msg = err.response?.data?.message || 'Failed to retry job'
      Toast(msg, 'danger', '')
    }
  }

  const handleCancel = async (id: string | number) => {
    try {
      await cancelImportJob(axiosPrivate, id)
      Toast('Job cancelation initiated successfully', 'success', '')
      fetchJobs()
    } catch (err: any) {
      console.error(err)
      const msg = err.response?.data?.message || 'Failed to cancel job'
      Toast(msg, 'danger', '')
    }
  }

  const handleRevert = async (id: string | number, isComtrade: boolean = false) => {
    const title = isComtrade
      ? 'Are you sure you want to revert this synchronization?'
      : 'Are you sure you want to revert this import?'
    const text = isComtrade
      ? 'This will restore the previous backup and delete the synchronized trade data.'
      : 'This will delete all inserted records from this import job.'

    showSwal({
      title,
      text,
      icon: 'question',
    }).then(async (result) => {
      if (!result.isConfirmed) return

      try {
        if (isComtrade) {
          await revertComtradeJob(axiosPrivate, id)
        } else {
          await revertImportJob(axiosPrivate, id)
        }
        Toast('Job data reverted successfully', 'success', '')
        fetchJobs()
      } catch (err: any) {
        console.error(err)
        const msg = err.response?.data?.message || 'Failed to revert job'
        Toast(msg, 'danger', '')
      }
    })
  }

  const getStatusBadge = (job: any) => {
    if (job.status === 'failed' && job.failedReason === 'Canceled by user') {
      return (
        <Badge bg='secondary' text='dark'>
          Canceled
        </Badge>
      )
    }

    switch (job.status) {
      case 'completed':
        return <Badge bg='success'>Success</Badge>
      case 'failed':
        return <Badge bg='danger'>Failed</Badge>
      case 'active':
        return <Badge bg='primary'>In Progress</Badge>
      case 'waiting':
      case 'delayed':
      default:
        return (
          <Badge bg='warning' text='dark'>
            Pending
          </Badge>
        )
    }
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        File Upload Queue
      </PageTitle>

      <Row>
        <Col>
          <Card>
            <Card.Header className='card-header-stretch'>
              <Card.Title className='w-100'>
                <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                  <div className='title d-flex align-items-center'>
                    <span className='fa fa-list-check px-2'></span>
                    <h3 className='text-gray-700 m-0'>File Upload Queue</h3>
                  </div>
                  <Button variant='light' size='sm' onClick={fetchJobs} disabled={isLoading}>
                    <i className={`fa fa-refresh ${isLoading ? 'fa-spin' : ''}`}></i> Refresh
                  </Button>
                </div>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div className='table-responsive'>
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='max-w-70px'>Job ID</th>
                      <th className='max-w-140px'>Type</th>
                      <th className='max-w-120px'>Uploader</th>
                      <th className='max-w-120px'>Created At</th>
                      <th className='max-w-120px'>Status</th>
                      <th className='max-w-120px'>Progress</th>
                      <th className='min-w-90px'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.length === 0 ? (
                      <tr>
                        <td colSpan={7} className='text-center text-muted py-5'>
                          No queue jobs found
                        </td>
                      </tr>
                    ) : (
                      jobs.map((job) => {
                        const progressData =
                          typeof job.progress === 'object' && job.progress !== null
                            ? {
                                percent: job.progress.percent || 0,
                                message: job.progress.message || '',
                              }
                            : {
                                percent:
                                  typeof job.progress === 'number'
                                    ? job.progress
                                    : parseInt(job.progress) || 0,
                                message: '',
                              }
                        return (
                          <tr key={job.id}>
                            <td>
                              <span className='text-dark fw-bold fs-6'>{job.id}</span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold text-hover-primary fs-6'>
                                {job.data?.type?.toUpperCase() || '-'}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {job.data?.username || '-'}
                              </span>
                            </td>
                            <td>
                              <span className='text-muted fw-semibold d-block fs-7'>
                                {moment(job.timestamp).format('DD MMM YYYY, HH:mm')}
                              </span>
                            </td>
                            <td>{getStatusBadge(job)}</td>
                            <td>
                              <div className='d-flex flex-column w-100 me-2'>
                                <div className='d-flex flex-stack mb-2'>
                                  <span className='text-muted me-2 fs-7 fw-semibold'>
                                    {progressData.percent}%
                                  </span>
                                </div>
                                <ProgressBar
                                  now={progressData.percent}
                                  variant={
                                    job.status === 'failed'
                                      ? 'danger'
                                      : job.status === 'completed'
                                      ? 'success'
                                      : 'primary'
                                  }
                                  style={{height: '5px'}}
                                />
                                {progressData.message && (
                                  <span
                                    className='text-muted fs-8 mt-1'
                                    style={{maxWidth: '200px', wordWrap: 'break-word'}}
                                  >
                                    {progressData.message}
                                  </span>
                                )}
                                {job.failedReason && (
                                  <span
                                    className='text-danger fs-8 mt-1'
                                    style={{maxWidth: '200px', wordWrap: 'break-word'}}
                                  >
                                    {job.failedReason}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              {job.status === 'failed' && job.failedReason !== 'Canceled by user' && (
                                <Button
                                  variant='light-primary'
                                  size='sm'
                                  onClick={() => handleRetry(job.id)}
                                >
                                  Retry
                                </Button>
                              )}
                              {['waiting', 'active', 'delayed'].includes(job.status) && (
                                <Button
                                  variant='light-danger'
                                  size='sm'
                                  onClick={() => handleCancel(job.id)}
                                >
                                  Cancel
                                </Button>
                              )}
                              {job.status === 'completed' && (
                                <>
                                  {job.name === 'importTariff' && job.returnValue?.startId != null && (
                                    <Button
                                      variant='light-danger'
                                      size='sm'
                                      onClick={() => handleRevert(job.id, false)}
                                      disabled={job.data?.isReverted}
                                    >
                                      {job.data?.isReverted ? 'Reverted' : 'Revert'}
                                    </Button>
                                  )}
                                  {job.name === 'fetchComtradeData' && job.returnValue?.hasBackup === true && (
                                    <Button
                                      variant='light-danger'
                                      size='sm'
                                      onClick={() => handleRevert(job.id, true)}
                                      disabled={job.data?.isReverted}
                                    >
                                      {job.data?.isReverted ? 'Reverted' : 'Revert'}
                                    </Button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default FileUploadQueuePage
